import React from "react";
import { isUnpricedRelease, staffName } from "../components/releaseTypes";

/**
 * Simple printable pharmacy receipt for a ProductsRelease — used for
 * standalone (Patient Release) sales, which previously had no printout.
 * Releases settled on a treatment invoice carry that receipt number.
 */

interface ReleaseReceiptProps {
  clinic?: {
    title?: string;
    address?: string;
    tpNo?: string;
  } | null;
  release: any;
}

const cell: React.CSSProperties = {
  borderBottom: "1px solid #ccc",
  padding: "4px 6px",
  fontSize: 12,
  textAlign: "left",
};

const TITLES: Record<string, string> = {
  patient: "Pharmacy Receipt",
  employee: "Staff Purchase Receipt",
  clinic: "Clinic Use Note",
  treatment: "Treatment Use Note",
};

function ReleaseReceipt({ clinic, release }: ReleaseReceiptProps) {
  const lines = release?.product || [];
  // Clinic / treatment use: what was taken and by whom, no prices or payment
  const priced = !isUnpricedRelease(release?.type);
  const isTreatment = release?.type === "treatment";
  const person =
    release?.patientId?.fullName || staffName(release?.employeeId) || "";
  const date = String(release?.createdAt || "").split("T")[0];
  const receiptNo =
    release?.invoiceNo ||
    `REL-${String(release?._id || "").slice(-6).toUpperCase()}`;

  return (
    <div
      style={{
        width: 400,
        padding: 16,
        background: "white",
        color: "black",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 16, fontWeight: "bold" }}>
          {clinic?.title || ""}
        </div>
        {clinic?.address && <div style={{ fontSize: 11 }}>{clinic.address}</div>}
        {clinic?.tpNo && <div style={{ fontSize: 11 }}>Tel: {clinic.tpNo}</div>}
        <div style={{ fontSize: 13, fontWeight: "bold", marginTop: 6 }}>
          {TITLES[release?.type] || TITLES.patient}
        </div>
      </div>

      <div style={{ fontSize: 12, marginBottom: 8 }}>
        <div>
          {priced ? "Receipt" : "Release"} No: <b>{receiptNo}</b>
        </div>
        {person && (
          <div>
            {priced ? "Name" : isTreatment ? "Patient" : "Taken by"}: {person}
          </div>
        )}
        {release?.patientId?.regNo && (
          <div>Reg No: {release.patientId.regNo}</div>
        )}
        {isTreatment && (release?.treatmentName || release?.stage) && (
          <div>
            Treatment: {[release.treatmentName, release.stage].filter(Boolean).join(" · ")}
          </div>
        )}
        {isTreatment && staffName(release?.employeeId) && (
          <div>Done by: {staffName(release.employeeId)}</div>
        )}
        <div>Date: {date}</div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={cell}>Item</th>
            <th style={{ ...cell, textAlign: "center" }}>Qty</th>
            {priced && <th style={{ ...cell, textAlign: "right" }}>Unit</th>}
            {priced && <th style={{ ...cell, textAlign: "right" }}>Total</th>}
          </tr>
        </thead>
        <tbody>
          {lines.map((line: any, i: number) => (
            <tr key={i}>
              <td style={cell}>
                {line?.productId?.productName ||
                  line?.productName ||
                  String(line?.productId || "")}
              </td>
              <td style={{ ...cell, textAlign: "center" }}>
                {line?.quantity}
              </td>
              {priced && (
                <td style={{ ...cell, textAlign: "right" }}>
                  {line?.unitPrice}
                </td>
              )}
              {priced && (
                <td style={{ ...cell, textAlign: "right" }}>{line?.total}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {priced && (
        <table style={{ width: "100%", marginTop: 8, fontSize: 12 }}>
          <tbody>
            <tr>
              <td>Total</td>
              <td style={{ textAlign: "right" }}>Rs. {release?.total ?? 0}</td>
            </tr>
            {Number(release?.discount) > 0 && (
              <tr>
                <td>Discount</td>
                <td style={{ textAlign: "right" }}>Rs. {release?.discount}</td>
              </tr>
            )}
            <tr style={{ fontWeight: "bold" }}>
              <td>Net Total</td>
              <td style={{ textAlign: "right" }}>Rs. {release?.netTotal ?? 0}</td>
            </tr>
            {Number(release?.cash) > 0 && (
              <tr>
                <td>Cash</td>
                <td style={{ textAlign: "right" }}>Rs. {release?.cash}</td>
              </tr>
            )}
            {Number(release?.card) > 0 && (
              <tr>
                <td>Card</td>
                <td style={{ textAlign: "right" }}>Rs. {release?.card}</td>
              </tr>
            )}
            {Number(release?.bankTransfer) > 0 && (
              <tr>
                <td>Bank Transfer</td>
                <td style={{ textAlign: "right" }}>Rs. {release?.bankTransfer}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {priced ? (
        <div style={{ textAlign: "center", fontSize: 10, marginTop: 10 }}>
          Thank you!
        </div>
      ) : (
        <div style={{ fontSize: 11, marginTop: 24 }}>
          Signature: ______________________
        </div>
      )}
    </div>
  );
}

export default ReleaseReceipt;
